import axios from 'axios';
import { toast } from 'react-toastify';
import { shouldRefreshToken, getTokenAgeMinutes, generateNewToken } from './AuthService';

const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  withCredentials: true,
});

let isRefreshing = false;
let failedQueue = [];

/** Email / notification deep links — never bounce back into these after a forced login. */
function isResourceDeepLinkPath(path = '') {
  return /\/(task\/taskdetails|forum\/detail|documents\/details)(\/|$)/i.test(path);
}

function clearRedirectAfterLogin() {
  try {
    sessionStorage.removeItem('redirectAfterLogin');
  } catch (e) {
    /* ignore */
  }
}

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

axiosInstance.interceptors.request.use(async (config) => {
  // Check if token needs refresh based on 15-minute rule
  if (shouldRefreshToken()) {
    const tokenAge = getTokenAgeMinutes();
    console.log(`[Token Refresh Check] Token is ${tokenAge} minutes old, refreshing...`);
    
    try {
      const response = await generateNewToken();
      const newToken = response.data.accessToken;
      const apiTimestamp = response.data.timestamp || response.data.issuedAt || response.data.createdAt;
      
      // Update stored token and timestamp
      const rememberMe = localStorage.getItem('rememberMe') === 'true';
      if (rememberMe) {
        localStorage.setItem('accessToken', JSON.stringify(newToken));
        localStorage.setItem('accessTokenTimestamp', (apiTimestamp || Date.now()).toString());
      } else {
        localStorage.setItem('tempAccessToken', JSON.stringify(newToken));
        localStorage.setItem('tempAccessTokenTimestamp', (apiTimestamp || Date.now()).toString());
        sessionStorage.setItem('accessToken', JSON.stringify(newToken));
      }
      
      console.log('[Token Refresh] Successfully refreshed token');
      config.headers.Authorization = `Bearer ${newToken}`;
    } catch (error) {
      console.error('[Token Refresh] Failed to refresh token:', error);
      
      // If generateNewToken got 401 and rememberMe is true, try refresh-token endpoint
      const rememberMe = localStorage.getItem('rememberMe') === 'true';
      if (error.response?.status === 401 && rememberMe) {
        console.log('[Token Refresh] Got 401 from generateNewToken, attempting /auth/refresh-token...');
        
        try {
          const refreshResponse = await axios.post(
            `${process.env.REACT_APP_API_URL}/auth/refresh-token`,
            {},
            { withCredentials: true }
          );
          
          const newToken = refreshResponse.data.accessToken;
          const apiTimestamp = refreshResponse.data.timestamp || refreshResponse.data.issuedAt || refreshResponse.data.createdAt;
          const timestamp = apiTimestamp || Date.now();
          
          // Store refreshed token in localStorage
          localStorage.setItem('accessToken', JSON.stringify(newToken));
          localStorage.setItem('accessTokenTimestamp', timestamp.toString());
          
          console.log('[Token Refresh] Successfully refreshed token using /auth/refresh-token');
          config.headers.Authorization = `Bearer ${newToken}`;
        } catch (refreshError) {
          console.error('[Token Refresh] Failed to refresh using /auth/refresh-token:', refreshError);
          // Continue with existing token if both refresh attempts fail
        }
      }
      // For other errors or rememberMe false, continue with existing token
    }
  }

  // Get token based on rememberMe status
  const rememberMe = localStorage.getItem('rememberMe') === 'true';
  let token = null;
  
  if (rememberMe) {
    // Remember Me: Use localStorage
    token = localStorage.getItem('accessToken');
  } else {
    token = localStorage.getItem('tempAccessToken');
  }
  
  if (token) {
    try {
      const parsedToken = JSON.parse(token);
      config.headers.Authorization = `Bearer ${parsedToken}`;
    } catch (e) {
      console.error('Error parsing token:', e);
    }
  }

  // Only set JSON content type if not FormData
  const isFormData = config.data instanceof FormData;
  if (!isFormData) {
    config.headers['Content-Type'] = 'application/json';
  }

  console.log('[Request] Token attached:', token ? 'present' : 'missing');
  return config;
});


axiosInstance.interceptors.response.use(
  (res) => res,
  async (error) => {
    const originalRequest = error.config;

    console.log('[Response Error]', error?.response?.status, 'URL:', originalRequest?.url);

    // Handle status code 498: Password changed, force re-login
    if (error.response?.status === 498) {
      toast.error('Password is changed', {
        onClose: () => {
          window.location.href = '/login';
        },
      });
      // Remove token from storage
      localStorage.removeItem('accessToken');
      localStorage.removeItem('user');
      localStorage.removeItem('userDetails');
      sessionStorage.removeItem('accessToken');
      
      // Set logout flag to trigger logout in all tabs
      localStorage.setItem('logout', Date.now());
      
      return Promise.reject(error);
    }

    // 403 = permission / resource access denied for the current user (e.g. another user's email deep link).
    // Never treat it as session expiry — that logs the user out and can redirect-loop after re-login.
    if (error.response?.status === 403 || error.response?.status === 409) {
      if (isResourceDeepLinkPath(window.location.pathname)) {
        clearRedirectAfterLogin();
      }
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      console.log('[Retry Triggered] Retrying original request:', originalRequest.url);

      // Check if rememberMe is false - if so, don't refresh token, just redirect to login
      const rememberMe = localStorage.getItem('rememberMe') === 'true';
      
      if (!rememberMe) {
        console.log('[No Remember Me] Token expired, redirecting to login instead of refreshing');
        
        // Clear all authentication data
        localStorage.removeItem('accessToken');
        localStorage.removeItem('accessTokenTimestamp');
        localStorage.removeItem('tempAccessToken');
        localStorage.removeItem('tempAccessTokenTimestamp');
        localStorage.removeItem('user');
        localStorage.removeItem('userDetails');
        localStorage.removeItem('rememberMe');
        sessionStorage.removeItem('accessToken');
        sessionStorage.removeItem('tabCount');
        sessionStorage.removeItem('isFirstSession');
        
        // Set logout flag to trigger logout in all tabs
        localStorage.setItem('logout', Date.now());

        const currentPath = window.location.pathname + window.location.search;
        const isLoginPage = currentPath === '/login' || currentPath.startsWith('/login?');
        // Do not preserve email deep links — if access fails after login, it causes a logout loop
        if (!isLoginPage && !isResourceDeepLinkPath(currentPath)) {
          sessionStorage.setItem('redirectAfterLogin', currentPath);
        } else {
          clearRedirectAfterLogin();
        }
        if (!isLoginPage) {
          window.location.href = '/login';
        }

        return Promise.reject(error);
      }

      // If rememberMe is true, proceed with token refresh and queue management
      if (isRefreshing) {
        console.log('[Token Refreshing in Progress] Queuing request:', originalRequest.url);
        return new Promise((resolve, reject) => {
          failedQueue.push({
            resolve: (token) => {
              console.log('[Queue Resolved] Retrying with new token for:', originalRequest.url);
              originalRequest.headers['Authorization'] = `Bearer ${token}`;
              resolve(axiosInstance(originalRequest));
            },
            reject: (err) => {
              console.log('[Queue Rejected] Request failed after refresh:', originalRequest.url);
              reject(err);
            },
          });
        });
      }

      isRefreshing = true;
      console.log('[Refreshing Token]');

      return new Promise(async (resolve, reject) => {
        try {
          const res = await axios.post(
            `${process.env.REACT_APP_API_URL}/auth/refresh-token`,
            {},
            { withCredentials: true }
          );

          const newToken = res.data.accessToken;
          // Use API timestamp if available, otherwise fallback to current time
          const apiTimestamp = res.data.timestamp || res.data.issuedAt || res.data.createdAt;
          const timestamp = apiTimestamp || Date.now();
          
          // Store refreshed token in localStorage (rememberMe is true at this point)
          localStorage.setItem('accessToken', JSON.stringify(newToken));
          localStorage.setItem('accessTokenTimestamp', timestamp.toString());

          // Optional: Update global default
          axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;

          processQueue(null, newToken);

          originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
          resolve(axiosInstance(originalRequest));
        } catch (err) {
          processQueue(err, null);
          localStorage.removeItem('accessToken');
          localStorage.removeItem('accessTokenTimestamp');
          localStorage.removeItem('tempAccessToken');
          localStorage.removeItem('tempAccessTokenTimestamp');
          localStorage.removeItem('user');
          localStorage.removeItem('userDetails');
          localStorage.removeItem('rememberMe');
          sessionStorage.removeItem('accessToken');
          sessionStorage.removeItem('tabCount');
          sessionStorage.removeItem('isFirstSession');
          
          // Set logout flag to trigger logout in all tabs
          localStorage.setItem('logout', Date.now());

          const currentPath = window.location.pathname + window.location.search;
          const isLoginPage = currentPath === '/login' || currentPath.startsWith('/login?');
          if (!isLoginPage && !isResourceDeepLinkPath(currentPath)) {
            sessionStorage.setItem('redirectAfterLogin', currentPath);
          } else {
            clearRedirectAfterLogin();
          }
          
          toast.error('Session expired. Please log in again.', {
            onClose: () => {
              window.location.href = '/login';
            },
          });

          reject(err);
        } finally {
          isRefreshing = false;
        }
      });
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;
